<template>
    <div>
        <b-tabs small pills card :navWrapperClass="'golia-pills-tabs'">
            <!-- GENERAL SETTINGS -->
            <b-tab active>
                <template #title>
                    <i class="fa-solid fa-gear"></i> {{ __('options_general') }}
                </template>

                <!-- LANG SETTINGS -->
                <h5 class="section-title mt-4 mb-2">{{ __('options_language') }}</h5>

                <div class="bg-main-inner-bg p-3 rounded-3">
                    <div class="row">
                        <div class="col-lg-4">
                            <div class="form-group mb-3">
                                <label class="mb-2">{{ __('change_interface_language') }}</label>
                                <v-select
                                    v-model="langs['lingua']"
                                    :options="userSettings.languages"
                                    label="DESCRIZIONE"
                                    :reduce="(option) => option.TRANSLATION_LANGUAGE"
                                    :placeholder="__('select')"
                                    :clearable="false"
                                    :searchable="false"
                                ></v-select>
                            </div>
                        </div>

                        <div class="col-lg-4">
                            <div class="form-group mb-3">
                                <label class="mb-2">{{ __('change_timezone') }}</label>
                                <v-select
                                    v-model="langs['timeZone']"
                                    :options="userSettings.timezones"
                                    label="timeZone"
                                    :reduce="(option) => option.timeZone"
                                    :placeholder="__('select')"
                                    :clearable="false"
                                    :searchable="false"
                                ></v-select>
                            </div>
                        </div>

                        <div class="col-lg-4">
                            <div class="form-group mb-3">
                                <label class="mb-2">{{ __('change_infringment_language') }}</label>
                                <v-select
                                    v-model="langs['infrNation']"
                                    :options="userSettings.infringment_nations"
                                    label="NAME"
                                    :reduce="(option) => option.CODE"
                                    :placeholder="__('select')"
                                    :clearable="false"
                                    :searchable="false"
                                ></v-select>
                            </div>
                        </div>
                    </div>

                    <div class="form-group">
                        <golia-button
                            :label="__('save_language_settings')"
                            variant="btn-fill success"
                            :loading="loading"
                            @click="changeLanguage()"
                        >
                        </golia-button>
                    </div>
                </div>

                <!-- COLOR MODES -->
                <h5 class="section-title mt-4 mb-2">{{ __('options_general') }}</h5>

                <div class="bg-main-inner-bg p-3 rounded-3">
                    <label class="d-block mb-2">{{ __('select_color_mode') }}</label>
                    <DarkMode :modes="['light', 'dark']">
                        <template v-slot="{ mode }">
                            <golia-button
                                :icon="iconMode(mode)"
                                variant="btn-fill dark-blue"
                            ></golia-button>
                            <span class="text-smaller ms-2">{{ labelMode(mode) }}</span>
                        </template>
                    </DarkMode>
                </div>
            </b-tab>

            <!-- VECHICLE LIST SETTINGS -->
            <b-tab lazy>
                <template #title>
                    <i class="fa-solid fa-list-ul"></i> {{ __('vehicle_list_options') }}
                </template>

                <!-- VEHICLE LIST MODE VIEW -->
                <h5 class="section-title mt-4 mb-2">{{ __('select_view') }}</h5>

                <div class="bg-main-inner-bg p-3 rounded-3">
                    <div class="form-group d-flex align-items-center gap-2">
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                id="list"
                                type="radio"
                                v-model="vehicleList.viewMode"
                                :value="'list'"
                            />
                            <label class="form-check-label" for="list">{{ __('list') }}</label>
                        </div>

                        <div class="form-check">
                            <input
                                class="form-check-input"
                                id="table"
                                type="radio"
                                v-model="vehicleList.viewMode"
                                :value="'table'"
                            />
                            <label class="form-check-label" for="table">{{ __('table') }}</label>
                        </div>
                    </div>
                </div>

                <!-- VEHICLE LIST MODE VIEW (RELATED OPTIONS) -->
                <h5 class="section-title mt-4 mb-2">
                    {{ __('view_options') }} "{{ __(vehicleList.viewMode) }}"
                </h5>

                <div class="bg-main-inner-bg p-3 rounded-3">
                    <template v-if="vehicleList.viewMode === 'table'">
                        <golia-multiselect
                            label="label"
                            track-by="value"
                            :value="tableFields"
                            :emptyLabel="__('empty_search')"
                            :placeholder="__('select_fields_placeholder') + '...'"
                            :selectLabel="__('select')"
                            :selectedLabel="__('selected')"
                            :deselectLabel="__('deselect')"
                            :options="vehicleList.tableFieldsOptions"
                            :show-labels="false"
                            :multiple="true"
                            :close-on-select="false"
                            :customSelectedLabel="{
                                singular: __('selected_field'),
                                plural: __('selected_fields')
                            }"
                            :outsideTagsSelected="true"
                            :customValueLabel="{
                                singular: __('selected_field'),
                                plural: __('selected_fields')
                            }"
                            id="vehicleListTableFields_Options"
                            refer="vehicleListTableFields_Options"
                            @input="saveTableFields"
                        >
                        </golia-multiselect>

                        <hr />

                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="vehicleList.fluidTable"
                                id="fluidTable"
                                @change="
                                    saveOption('vehicleListFluidTable', vehicleList.fluidTable)
                                "
                            />
                            <label class="form-check-label" for="fluidTable">
                                {{ __('fluid_table') }}
                            </label>
                        </div>
                        <small class="form-text text-tiny">
                            {{ __('fluid_table_descr') }}
                        </small>
                    </template>

                    <template v-else>
                        <div class="dummy-main-panel my-4">
                            <truck-list-item class="truck-list-item" :truck="dummy_data.truck" />
                        </div>

                        <div class="row">
                            <div class="col-5">
                                <div class="form-group my-3">
                                    <p
                                        class="text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                                    >
                                        - {{ __('vehicle_identification') }}
                                    </p>

                                    <div class="form-group">
                                        <div
                                            v-for="(
                                                option, index
                                            ) in vehicleList.vehicleIdentificationOptions"
                                            :key="index"
                                            class="form-check"
                                        >
                                            <input
                                                class="form-check-input"
                                                type="radio"
                                                :id="option.value || 'vehicle-default'"
                                                :value="option.value"
                                                v-model="vehicleList.vehicleIdentification"
                                                @change="
                                                    saveOption(
                                                        'vehicleIdentification',
                                                        vehicleList.vehicleIdentification
                                                    )
                                                "
                                            />
                                            <label
                                                class="form-check-label"
                                                :for="option.value || 'vehicle-default'"
                                                >{{ option.label }}</label
                                            >
                                        </div>
                                    </div>

                                    <hr class="divider black thin" />

                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            v-model="vehicleList.copyIdentificationToMarker"
                                            id="identification-to-marker"
                                            @change="
                                                saveOption(
                                                    'copyIdentificationToMarker',
                                                    vehicleList.copyIdentificationToMarker
                                                )
                                            "
                                        />
                                        <label
                                            class="form-check-label"
                                            for="identification-to-marker"
                                        >
                                            {{ __('apply_to_marker') }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div class="col-6 ms-auto">
                                <div class="form-group my-3">
                                    <p
                                        class="text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                                    >
                                        - {{ __('vehicle_fields_list') }}
                                    </p>

                                    <div class="row">
                                        <div
                                            class="col-6"
                                            v-for="(
                                                option, index
                                            ) in vehicleList.truckListItemFieldsOptions"
                                            :key="index"
                                        >
                                            <div class="form-check">
                                                <input
                                                    class="form-check-input"
                                                    :disabled="shouldCheckboxDisabled(option.value)"
                                                    :id="option.value"
                                                    type="checkbox"
                                                    :value="option.value"
                                                    v-model="vehicleList.truckListItemFields"
                                                    @change="
                                                        saveOption(
                                                            'truckListItemFields',
                                                            vehicleList.truckListItemFields
                                                        )
                                                    "
                                                />
                                                <label class="form-check-label" :for="option.value">
                                                    {{ option.label }}
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <b-alert
                                        class="mt-3"
                                        variant="info"
                                        show
                                        style="font-size: 14px; padding: 8px 16px"
                                    >
                                        {{ __('vehicle_fields_list_warning') }}
                                    </b-alert>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </b-tab>

            <!-- MAP SETTINGS -->
            <b-tab lazy>
                <template #title>
                    <i class="fa-solid fa-map-location-dot"></i> {{ __('options_map') }}
                </template>

                <!-- VEHICLES MARKER CLUSTERING ON MAP -->
                <h5 class="section-title mt-4 mb-2">{{ __('options_map') }}</h5>

                <div class="bg-main-inner-bg p-3 rounded-3">
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            v-model="map.activeVehicleClustering"
                            id="activeVehicleClustering"
                            @change="saveVehicleClustering"
                        />
                        <label class="form-check-label" for="activeVehicleClustering">
                            {{ __('vehicles_clustering') }}
                        </label>
                    </div>
                </div>

                <!-- VEHICLES MARKER GRAPHIC OPTIONS -->
                <h5 class="section-title mt-4 mb-2">{{ __('options_marker') }}</h5>

                <div class="bg-main-inner-bg p-3 rounded-3">
                    <div class="row">
                        <div class="col-lg-7">
                            <!-- MARKER SIZE (small, medium, large) -->
                            <p
                                class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                            >
                                {{ __('size') }}
                            </p>

                            <div class="form-group d-flex align-items-center gap-2 mb-4">
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="marker-small"
                                        v-model="map.vehicleMarkerSize"
                                        :value="[40, 54]"
                                        @change="
                                            saveOption('vehicleMarkerSize', map.vehicleMarkerSize)
                                        "
                                    />
                                    <label class="form-check-label" for="marker-small">
                                        {{ __('small') }}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="marker-medium"
                                        v-model="map.vehicleMarkerSize"
                                        :value="[52, 66]"
                                        @change="
                                            saveOption('vehicleMarkerSize', map.vehicleMarkerSize)
                                        "
                                    />
                                    <label class="form-check-label" for="marker-medium">
                                        {{ __('medium') }}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="marker-large"
                                        v-model="map.vehicleMarkerSize"
                                        :value="[60, 74]"
                                        @change="
                                            saveOption('vehicleMarkerSize', map.vehicleMarkerSize)
                                        "
                                    />
                                    <label class="form-check-label" for="marker-large">
                                        {{ __('large') }}
                                    </label>
                                </div>
                            </div>

                            <!-- MARKER FONT SIZE (small, medium, large) -->
                            <p
                                class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                            >
                                {{ __('font_size') }}
                            </p>

                            <div class="form-group d-flex align-items-center gap-2 mb-4">
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="text-marker-small"
                                        v-model="map.vehicleMarkerFontSize"
                                        :value="'8px'"
                                        @change="
                                            saveOption(
                                                'vehicleMarkerFontSize',
                                                map.vehicleMarkerFontSize
                                            )
                                        "
                                    />
                                    <label class="form-check-label" for="text-marker-small">
                                        {{ __('small') }}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="text-marker-medium"
                                        v-model="map.vehicleMarkerFontSize"
                                        :value="'10px'"
                                        @change="
                                            saveOption(
                                                'vehicleMarkerFontSize',
                                                map.vehicleMarkerFontSize
                                            )
                                        "
                                    />
                                    <label class="form-check-label" for="text-marker-medium">
                                        {{ __('medium') }}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        id="text-marker-large"
                                        v-model="map.vehicleMarkerFontSize"
                                        :value="'12px'"
                                        @change="
                                            saveOption(
                                                'vehicleMarkerFontSize',
                                                map.vehicleMarkerFontSize
                                            )
                                        "
                                    />
                                    <label class="form-check-label" for="text-marker-large">
                                        {{ __('large') }}
                                    </label>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-4">
                                    <!-- MARKER BACKGROUND COLOR -->
                                    <p
                                        class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                                    >
                                        {{ __('background_color') }}
                                    </p>

                                    <div class="form-group mb-3">
                                        <input
                                            v-model="map.vehicleMarkerBackgroundColor"
                                            type="color"
                                            class="form-control form-control-color"
                                            @blur="
                                                saveOption(
                                                    'vehicleMarkerBackgroundColor',
                                                    map.vehicleMarkerBackgroundColor
                                                )
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="col-4">
                                    <!-- MARKER FONT COLOR -->
                                    <p
                                        class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                                    >
                                        {{ __('font_color') }}
                                    </p>

                                    <div class="form-group mb-3">
                                        <input
                                            v-model="map.vehicleMarkerFontColor"
                                            type="color"
                                            class="form-control form-control-color"
                                            @blur="
                                                saveOption(
                                                    'vehicleMarkerFontColor',
                                                    map.vehicleMarkerFontColor
                                                )
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="col-4">
                                    <!-- MARKER OPACITY -->
                                    <p
                                        class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                                    >
                                        {{ __('opacity') }} %
                                    </p>

                                    <div class="form-group mb-3">
                                        <input
                                            v-model="map.vehicleMarkerOpacity"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="10"
                                            class="form-control"
                                            style="font-size: 14px"
                                            @input="
                                                saveOption(
                                                    'vehicleMarkerOpacity',
                                                    map.vehicleMarkerOpacity
                                                )
                                            "
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- MARKER ADDITIONAL INFO -->
                            <p
                                class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                            >
                                {{ __('additional_info') }}
                            </p>

                            <v-select
                                v-model="map.vehicleMarkerAdditionalData"
                                :options="markerAdditionalDataOptions"
                                :placeholder="__('select')"
                                :clearable="true"
                                :searchable="false"
                                @input="
                                    saveOption(
                                        'vehicleMarkerAdditionalData',
                                        map.vehicleMarkerAdditionalData?.value
                                    )
                                "
                            ></v-select>

                            <div class="row mt-3" v-if="map.vehicleMarkerAdditionalData">
                                <div class="col-4">
                                    <!-- ADDITIONAL INFO BACKGROUND COLOR -->
                                    <p
                                        class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                                    >
                                        - {{ __('background_color') }}
                                    </p>

                                    <div class="form-group mb-3">
                                        <input
                                            v-model="map.vehicleAdditionalInfoBackgroundColor"
                                            type="color"
                                            class="form-control form-control-color"
                                            @blur="
                                                saveOption(
                                                    'vehicleAdditionalInfoBackgroundColor',
                                                    map.vehicleAdditionalInfoBackgroundColor
                                                )
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="col-4">
                                    <!-- ADDITIONAL INFO FONT COLOR -->
                                    <p
                                        class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                                    >
                                        - {{ __('font_color') }}
                                    </p>

                                    <div class="form-group mb-3">
                                        <input
                                            v-model="map.vehicleAdditionalInfoFontColor"
                                            type="color"
                                            class="form-control form-control-color"
                                            @blur="
                                                saveOption(
                                                    'vehicleAdditionalInfoFontColor',
                                                    map.vehicleAdditionalInfoFontColor
                                                )
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="col-4">
                                    <!-- ADDITIONAL INFO OPACITY -->
                                    <p
                                        class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2"
                                    >
                                        - {{ __('opacity') }}
                                    </p>

                                    <div class="form-group mb-3">
                                        <input
                                            v-model="map.vehicleAdditionalInfoOpacity"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="10"
                                            class="form-control"
                                            style="font-size: 14px"
                                            @input="
                                                saveOption(
                                                    'vehicleAdditionalInfoOpacity',
                                                    map.vehicleAdditionalInfoOpacity
                                                )
                                            "
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-5" v-resized="resized">
                            <geofleet-map
                                class="geofleet-map smaller-controls rounded-3"
                                map="silica"
                                :trucks="[dummy_data.truck]"
                                :center="[
                                    dummy_data.truck.lastpoint.lat,
                                    dummy_data.truck.lastpoint.lng
                                ]"
                                :zoom="14"
                                :advanced="true"
                                :ptvToken="$env.ptv_password"
                                :extraControls="{}"
                                :noControls="true"
                                ref="settings_map"
                            >
                            </geofleet-map>
                        </div>
                    </div>
                </div>
            </b-tab>

            <!-- RESET SETTINGS BUTTON -->
            <template #tabs-end>
                <b-nav-item link-classes="bg-danger text-white" @click="$emit('resetSettings')">
                    <i class="fa-solid fa-trash-can"></i> {{ __('reset_settings') }}
                </b-nav-item>
            </template>
        </b-tabs>
    </div>
</template>

<script>
import L from 'leaflet'
import { mapState, mapActions } from 'vuex'
import TruckListItem from '@/views/geofleet/components/MainPanel/TruckListItem.vue'
import dummyTruck from '@/dummy_data/truck.js'

export default {
    name: 'GeoFleetSettings',
    props: {
        companies: {
            type: [Object, Array],
            default: () => {}
        }
    },
    components: {
        TruckListItem
    },
    data() {
        return {
            loading: false,
            vehicleList: {
                viewMode: 'list',
                tableFields: [
                    { value: 'status', label: this.__('status') },
                    { value: 'plate', label: this.__('plate') }
                ],
                tableFieldsOptions: [
                    { value: 'status', label: this.__('status') },
                    { value: 'plate', label: this.__('plate') },
                    { value: 'custom_id', label: this.__('custom_id') },
                    { value: 'activity', label: this.__('activity') },
                    { value: 'codriver', label: this.__('codriver') },
                    { value: 'speed', label: this.__('speed') },
                    { value: 'heading', label: this.__('heading') },
                    { value: 'altitude', label: this.__('altitude') },
                    { value: 'battery_level', label: this.__('battery_level') },
                    { value: 'fuel_level', label: this.__('fuel_level') },
                    { value: 'rpm_engine', label: this.__('rpm_engine') },
                    { value: 'ignition', label: this.__('ignition') },
                    { value: 'odometer', label: this.__('odometer') },
                    { value: 'gps_odometer', label: this.__('odometer_gps') },
                    { value: 'daily_distance', label: this.__('daily_distance') },
                    { value: 'gnss_status', label: this.__('gnss_status') },
                    { value: 'gsm_signal', label: this.__('gsm_signal') },
                    { value: 'address', label: this.__('address') },
                    { value: 'alerts', label: this.__('alerts') }
                ],
                fluidTable: false,

                vehicleIdentification: null,
                vehicleIdentificationOptions: [
                    { value: null, label: this.__('default') },
                    { value: 'plate', label: this.__('plate') },
                    { value: 'customId', label: this.__('custom_id') },
                    {
                        value: 'driver',
                        label:
                            this.__('name') + ' ' + this.__('surname') + ' - ' + this.__('driver')
                    }
                ],
                copyIdentificationToMarker: false,

                truckListItemFields: [],
                truckListItemFieldsOptions: [
                    { value: 'address', label: this.__('address') },
                    { value: 'speed', label: this.__('speed') },
                    { value: 'heading', label: this.__('heading') },
                    { value: 'altitude', label: this.__('altitude') },
                    {
                        value: 'fuel_battery_level',
                        label: this.__('fuel') + ' / ' + this.__('battery')
                    },
                    { value: 'rpm_engine', label: this.__('rpm_engine') },
                    { value: 'daily_distance', label: this.__('daily_distance') },
                    { value: 'gnss_status', label: this.__('gnss_status') },
                    { value: 'gsm_signal', label: this.__('gsm_signal') }
                ]
            },
            dummy_data: {
                truck: dummyTruck
            },
            map: {
                activeVehicleClustering: null,
                vehicleMarkerSize: [52, 66],
                vehicleMarkerFontSize: '10px',
                vehicleMarkerFontColor: null,
                vehicleMarkerBackgroundColor: null,
                vehicleMarkerOpacity: 50,
                vehicleMarkerAdditionalData: null,
                vehicleAdditionalInfoBackgroundColor: null,
                vehicleAdditionalInfoFontColor: null,
                vehicleAdditionalInfoOpacity: 50
            },
            markerAdditionalDataOptions: [
                {
                    label: this.__('speed'),
                    value: 'speed'
                },
                {
                    label: this.__('altitude'),
                    value: 'altitude'
                },
                {
                    label: this.__('rpm_engine'),
                    value: 'rpm_engine'
                }
            ],
            analysis: {
                chart: {
                    speed: {
                        active: true
                    },
                    activity: {
                        active: true,
                        top: '79%',
                        height: '25%',
                        opposite: true
                    },
                    altitude: {
                        active: true
                    },
                    comnsumption: {
                        active: true,
                        format: '{value} l'
                    }
                },
                hideTimeline: false
            },

            localCompanies: [],
            userSettings: {},
            langs: {
                infrNation: this.$authProcess.getParams('infringment_nation'),
                lingua: this.$authProcess.getParams('Lingua'),
                timeZone: this.$authProcess.getParams('timezone')
            }
        }
    },
    methods: {
        resized() {
            if (this.$refs.settings_map && this.$refs.settings_map.lmap)
                this.$refs.settings_map.lmap.invalidateSize(true)
        },
        getLangConfigurations() {
            this.$fetch(this.$env.GOLIA_API + '/golia/config')
                .then((res) => {
                    return res.json()
                })
                .then((data) => {
                    this.userSettings = data.data
                })

            if (this.langs && Object.entries(this.langs).length === 0) {
                this.langs['infrNation'] = this.$authProcess.getParams('infringment_nation')
                this.langs['lingua'] = this.$authProcess.getParams('Lingua')
                this.langs['timeZone'] = this.$authProcess.getParams('timezone')
            }
        },
        labelMode(mode) {
            return this.__(mode + '_mode')
        },
        iconMode(mode) {
            // modes: light, color_blind, dark, color_blind_dark
            if (mode === 'light') {
                return 'fa-solid fa-sun'
            } else if (mode === 'color_blind') {
                return 'fa-solid fa-low-vision'
            } else if (mode === 'dark') {
                return 'fa-solid fa-moon'
            } else if (mode === 'color_blind_dark') {
                return 'fa-solid fa-low-vision'
            }
        },
        changeCompany() {
            this.$fetch(this.$env.GOLIA_API + '/companies/change', {
                method: 'POST',
                body: JSON.stringify({
                    azienda: this.selectedCompany
                })
            }).then((response) => {
                if (response.status === 200) {
                    this.$store.commit('updateLoggedUserParams', {})
                    this.$store.commit('updateRulesLoaded', false)

                    setTimeout(() => {
                        window.location.reload()
                    }, 1000)
                }
            })
        },
        changeLanguage() {
            let lang = this.langs

            if (Object.entries(lang).length === 0) return

            this.loading = true

            // Call in put to change language
            this.$fetch(this.$env.GOLIA_API + '/users/me/config', {
                method: 'PUT',
                body: JSON.stringify(lang)
            })
                .then((response) => {
                    if (response.status === 200 || response.status === 204) {
                        this.$store.commit('updateLoggedUserParams', {})
                        this.$store.commit('updateRulesLoaded', false)

                        setTimeout(() => {
                            this.loading = false
                            window.location.reload()
                        }, 1000)
                    }
                })
                .catch((error) => {
                    this.loading = false
                    console.log(error)
                })
        },
        saveOption(option, value) {
            console.log(option, value)
            // If value is an Object update its properties instead of the entire object
            if (typeof value === 'object' && Array.isArray(value) === false && value !== null) {
                this.$store.dispatch('saveConfig', {
                    [option]: { ...this.config[option], ...value },
                    vueInstance: this
                })
                return
            }

            this.$store.dispatch('saveConfig', { [option]: value, vueInstance: this })
        },
        saveTableFields(fields) {
            this.saveOption('vehicleListTableFields', { fields: fields })
        },
        saveVehicleClustering() {
            this.$bvModal
                .msgBoxConfirm(this.__('vehicles_clustering_warning'), {
                    title: this.__('warning'),
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'success',
                    okTitle: this.__('yes'),
                    cancelTitle: this.__('no'),
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                })
                .then((value) => {
                    // Yes
                    if (value) {
                        this.saveOption('activeVehicleClustering', this.map.activeVehicleClustering)
                        location.reload()
                        // No
                    } else {
                        this.saveOption('activeVehicleClustering', this.map.activeVehicleClustering)
                    }
                })
                .catch(() => {
                    // Error
                })
        },
        shouldCheckboxDisabled(value) {
            if (value === 'address') return false

            return (
                this.truckListItemFieldsCount >= 3 &&
                !this.vehicleList.truckListItemFields.includes(value)
            )
        },
        resetData() {
            if (this.config) {
                // Vehicle List settings
                this.vehicleList.viewMode = this.config.vehicleListModeView || 'list'
                this.vehicleList.fluidTable = this.config.vehicleListFluidTable || false
                this.vehicleList.vehicleIdentification = this.config.vehicleIdentification || null
                this.vehicleList.copyIdentificationToMarker =
                    this.config.copyIdentificationToMarker || false
                this.vehicleList.truckListItemFields = this.config.truckListItemFields || [
                    'address',
                    'speed',
                    'fuel_battery_level',
                    'altitude'
                ]

                // Map settings
                this.map.activeVehicleClustering = this.config.activeVehicleClustering ?? true
                this.map.vehicleMarkerSize = this.config.vehicleMarkerSize ?? [52, 66]
                this.map.vehicleMarkerFontSize = this.config.vehicleMarkerFontSize ?? '10px'
                this.map.vehicleMarkerFontColor = this.config.vehicleMarkerFontColor ?? '#000000'
                this.map.vehicleMarkerBackgroundColor =
                    this.config.vehicleMarkerBackgroundColor ?? '#ffffff'
                this.map.vehicleMarkerOpacity = this.config.vehicleMarkerOpacity ?? 50
                this.map.vehicleMarkerAdditionalData =
                    {
                        value: this.config.vehicleMarkerAdditionalData,
                        label: this.__(this.config.vehicleMarkerAdditionalData)
                    } ?? null
                this.map.vehicleAdditionalInfoBackgroundColor =
                    this.config.vehicleAdditionalInfoBackgroundColor ?? '#ffffff'
                this.map.vehicleAdditionalInfoFontColor =
                    this.config.vehicleAdditionalInfoFontColor ?? '#000000'
                this.map.vehicleAdditionalInfoOpacity =
                    this.config.vehicleAdditionalInfoOpacity ?? 50

                // For every single property of this.config.chart set the relative this.analysis.chart property
                if (this.config.chart)
                    for (const [key, value] of Object.entries(this.config.chart)) {
                        if (this.analysis.chart[key]) this.analysis.chart[key] = value
                    }
            }
        },
        ...mapActions({
            logout: 'logout'
        })
    },
    created() {
        this.getLangConfigurations()

        // For chart settings to work properly, we need to save the config in the store at the beginning
        if (!this.config || (this.config && !this.config.chart))
            this.$store.dispatch('saveConfig', { chart: this.analysis.chart, vueInstance: this })

        this.resetData()
    },
    watch: {
        config: function () {
            this.resetData()
        },
        'vehicleList.viewMode': function (val) {
            this.saveOption('vehicleListModeView', val)

            if (val === 'table' && !this.config.vehicleListTableFields) {
                this.saveOption('vehicleListTableFields', { fields: this.tableFields })
            }
        }
    },
    computed: {
        tableFields() {
            return this.config?.vehicleListTableFields?.fields || this.vehicleList.tableFields
        },
        truckListItemFieldsCount() {
            let fields = this.vehicleList.truckListItemFields.filter(
                (field) =>
                    field !== 'address' && field !== 'fuel_level' && field !== 'battery_level'
            )

            return fields.length
        },
        ...mapState({
            config: (state) => state.geoFleet.config
        })
    }
}
</script>

<style lang="scss">
.dummy-main-panel {
    --side-panels-security-width: 380px;

    max-width: var(--side-panels-security-width);
    margin: 0 auto;

    .truck-list-item {
        background: var(--main-bg-color);
        border: 1px solid #ccc;
    }
}

input[type='color'].form-control.form-control-color {
    padding: 8px;
    border: 1px solid #d2d2d2;
    border-radius: 12px;
    height: calc(1.5em + 0.75rem);
    width: 100px;
}
</style>
